import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../Store/actions";
import { format, history } from "../../../common/lib";
import makeStyles from "@mui/styles/makeStyles";
import {
    List,
    ListItem,
    ListItemText,
    Typography,
    Button,
    CircularProgress
} from "@mui/material/";
import { strings } from "../../../common/i18n";
import { paths } from "../../Routes";

import styles from "./styles";
import { useNavigate } from "react-router-dom";
import { logToSentry } from "../../../config/Sentry";
import hasMissingAuditorPicks from "./Auditors/hasMissingAuditorPicks";

//TODO: this component needs refactoring
export default () => {
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    let navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { auditsCreate, auditGroups, accounts, typesList } = useSelector(
        (state) => state
    );

    const { filters, assetsToAudit } = auditsCreate;
    const filtersRes = {
        assetTypes: "",
        makes: "",
        categories: "",
        financeTypes: ""
    };

    // using object to avoid duplicates since multiple location can have same branch
    let auditBranchSet = {};
    let auditLocationSet = {};
    auditsCreate.data.forEach((i) => {
        if (i.selected && Object.keys(auditBranchSet).length < 3)
            auditBranchSet[i.branch] = i.branchName;

        if (
            auditsCreate.selectedLocations.ids.includes(i.location) &&
            Object.keys(auditLocationSet).length < 3
        )
            auditLocationSet[i.location] = i.locationName;
    });

    auditBranchSet = Object.values(auditBranchSet);
    auditLocationSet = Object.values(auditLocationSet);

    const auditStartDate = auditsCreate.startDate;
    const auditEndDate = auditsCreate.endDate;

    // get list of auditors
    const auditAuditorData = auditsCreate.data
        .filter((item) => item.selected && item.userLocations)
        .map((i) => i.userLocations.map((el) => el.user))
        .flat(1)
        .slice(0, 3);

    const auditAuditorsList = auditAuditorData.map((item) => {
        if (auditsCreate.auditors.length > 0) {
            const auditorNames = accounts.data
                .filter((el) => el.id === item)
                .map((el) => el.firstName + " " + el.surname);
            return auditorNames;
        }
    });

    // simplify filters for display
    Object.keys(filtersRes).forEach((filterName) => {
        if (typeof filters[filterName] === "object") {
            filtersRes[filterName] = filters[filterName]
                .slice(0, 2)
                .map((item) => {
                    const list = typesList[filterName].data
                        .filter((el) => el.id === item)
                        .map((i) => i.name);
                    return list;
                });
        }
    });

    const data = {
        [strings("dealer").toUpperCase()]: `${auditBranchSet}${
            auditBranchSet.length > 1 ? "..." : ""
        }`,
        [strings("location").toUpperCase()]: `${auditLocationSet}${
            auditLocationSet.length > 1 ? "..." : ""
        }`,
        [strings("auditor").toUpperCase()]: `${auditAuditorsList}${
            auditAuditorsList.join("").length > 1 ? "..." : ""
        }`
    };

    const handleSubmitAudit = async () => {
        const auditCreationData = auditsCreate.data.filter((i) =>
            auditsCreate.selectedLocations.ids.includes(i.location)
        );

        try {
            setLoading(true);
            await dispatch(
                actions.auditsCreate.create.update({ data: auditCreationData })
            );
            await dispatch(actions.auditsCreate.create.saveRequest());
            setLoading(false);
            dispatch(actions.appSettings.create.set({ auditsTabValue: 1 })); // switch tab to "Audits"
            navigate(`../${paths.scheduled}`);
        } catch (error) {
            console.error(error);
            logToSentry("On create audit submit:", { error });
            setLoading(false);
            dispatch(
                actions.toast.create.error(strings("errors.onCreateAudit"))
            );
        }
    };

    const disabled = useMemo(() => {
        // Checking auditGroups.item.id alone would wrongly block submission
        // for a recommended-but-not-yet-created group, which has id === null
        // by design (see audit-groups saga/reducer) -- it's a valid, selected
        // choice, not "nothing selected". But checking only that item is
        // truthy is ALSO wrong: clicking "Add" (itemAdd()) sets item to a
        // blank, unsaved {name: "", ...} template with no id and no
        // isRecommended flag -- that must still block submission, exactly as
        // it did before, or a half-finished "create a new group" attempt
        // could be submitted with no group ever actually chosen or created.
        const hasSelectedGroup =
            auditGroups.item &&
            (auditGroups.item.id || auditGroups.item.isRecommended);
        if (!hasSelectedGroup) {
            return true;
        }
        const selected = auditsCreate.data.filter((item) => item.selected);
        if (selected.length === 0) {
            return true;
        }
        /** Every audit CREATE would make needs at least one auditor picked on
         * the Auditors step — checked per selected audit, so an empty or stale
         * auditors list can't wave it through. See hasMissingAuditorPicks.js. */
        if (hasMissingAuditorPicks(auditsCreate.data, auditsCreate.auditors)) {
            return true;
        }
        /** "Specific assets" needs a VIN list. A MISSING list blocks CREATE
         * just as an empty one does: anything else creates an audit with no
         * asset filter at all — every asset at the location. */
        if (auditsCreate.assetsToAudit) {
            const vins = auditsCreate.filters && auditsCreate.filters.assetsToAudit;
            if (!Array.isArray(vins) || vins.length === 0) return true;
        }

        return false;
    }, [auditGroups, auditsCreate]);

    return (
        <div className={classes.root}>
            <Typography className={classes.title}>
                {strings("auditSummary").toUpperCase()}
            </Typography>

            <List component="nav" aria-label="main mailbox folders">
                {Object.keys(data).map((key) => {
                    return (
                        <ListItem
                            alignItems="flex-start"
                            className={classes.summaryListItem}
                            key={key}
                        >
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        className={classes.field}
                                    >
                                        {key}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        variant="subtitle2"
                                        color="primary"
                                    >
                                        {data[key] && data[key].length != 0
                                            ? data[key]
                                            : strings("none")}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    );
                })}

                <ListItem
                    alignItems="flex-start"
                    className={classes.summaryListItem}
                    key={"DUE DATE"}
                >
                    <ListItemText
                        primary={
                            <Typography type="body2" className={classes.field}>
                                {strings("auditDates").toUpperCase()}
                            </Typography>
                        }
                        secondary={
                            <Typography
                                variant="subtitle2"
                                className={classes.whiteSpace}
                                color="primary"
                            >
                                {`${strings("start")} : ${format.dateTimeNew(
                                    auditStartDate
                                )}\n${strings("finish")} : ${format.dateTimeNew(
                                    auditEndDate
                                )}`}
                            </Typography>
                        }
                    />
                </ListItem>

                {assetsToAudit ? (
                    <ListItem
                        alignItems="flex-start"
                        className={classes.summaryListItem}
                        key={"Assets"}
                    >
                        <ListItemText
                            primary={
                                <Typography
                                    type="body2"
                                    className={classes.field}
                                >
                                    {strings("assets").toUpperCase()}
                                </Typography>
                            }
                            secondary={
                                <Typography
                                    component="span"
                                    variant="subtitle2"
                                    className={classes.whiteSpace}
                                >
                                    {auditsCreate.filters.assetsToAudit &&
                                    auditsCreate.filters.assetsToAudit.length >
                                        0
                                        ? auditsCreate.assetsToAudit
                                        : strings("messages.assetsToAudit", {
                                              count: 0
                                          })}
                                </Typography>
                            }
                        />
                    </ListItem>
                ) : (
                    <ListItem
                        alignItems="flex-start"
                        className={classes.summaryListItem}
                        key={"DETAILS"}
                    >
                        <ListItemText
                            primary={
                                <Typography
                                    type="body2"
                                    className={classes.field}
                                >
                                    {strings("details").toUpperCase()}
                                </Typography>
                            }
                            secondary={
                                <>
                                    <Typography
                                        component="span"
                                        variant="subtitle2"
                                        className={classes.whiteSpace}
                                        color="primary"
                                    >
                                        {[
                                            `${filtersRes["assetTypes"]}${
                                                filtersRes["assetTypes"]
                                                    .length > 1
                                                    ? "..."
                                                    : ""
                                            }`,
                                            `${filtersRes["makes"]}${
                                                filtersRes["makes"].length > 1
                                                    ? "..."
                                                    : ""
                                            }`,
                                            `${filtersRes["categories"]}${
                                                filtersRes["categories"]
                                                    .length > 1
                                                    ? "..."
                                                    : ""
                                            }`,
                                            `${filtersRes["financeTypes"]}${
                                                filtersRes["financeTypes"]
                                                    .length > 1
                                                    ? "..."
                                                    : ""
                                            }`,
                                            `${strings(
                                                "assetRandomSampling"
                                            )} : ${
                                                filters["randomValueSampling"]
                                            }`,
                                            `${
                                                filters.conditions &&
                                                filters.conditions["isUsed"]
                                                    ? strings("usedAsset")
                                                    : ""
                                            }`,
                                            `${
                                                filters.conditions &&
                                                filters.conditions["isNew"]
                                                    ? strings("newAsset")
                                                    : ""
                                            }`,
                                            `${strings("monetaryValue")} : ${
                                                filters["monetaryValueSampling"]
                                            }`
                                        ].join("\r\n")}
                                    </Typography>
                                </>
                            }
                        />
                    </ListItem>
                )}
            </List>

            <div className={classes.auditBtnWrapper}>
                <Button
                    onClick={handleSubmitAudit}
                    color="primary"
                    variant="outlined"
                    disabled={disabled || loading}
                    className={classes.createAuditBtn}
                >
                    {strings("createAudit").toUpperCase()}
                </Button>
                {loading && (
                    <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                    />
                )}
            </div>
        </div>
    );
};
